import type { Budget, Category, Transaction, Wallet } from "./db";
import { assertUGX } from "./money";
import { APP_TIMEZONE, localDateKey } from "./time";

// Derived money values (project-description.md §8). Computed from rows,
// never stored, integers only. Deleted rows never count.

const live = <T extends { deleted_at: string | null }>(rows: T[]) =>
	rows.filter((r) => !r.deleted_at);

/**
 * opening + income − expense − fees − transfers out + transfers in (FN-2).
 * A fee is charged to the wallet the money left (`wallet_id`), whatever the kind.
 */
export function walletBalance(wallet: Wallet, transactions: Transaction[]): number {
	let balance = wallet.opening_balance;
	for (const t of live(transactions)) {
		if (t.wallet_id === wallet.id) {
			if (t.kind === "income") balance += t.amount;
			else balance -= t.amount; // expense, or a transfer out
			balance -= t.fee;
		}
		if (t.kind === "transfer" && t.to_wallet_id === wallet.id) balance += t.amount;
	}
	assertUGX(balance);
	return balance;
}

export function walletBalances(
	wallets: Wallet[],
	transactions: Transaction[],
): Map<string, number> {
	return new Map(live(wallets).map((w) => [w.id, walletBalance(w, transactions)]));
}

export type Allowance = {
	date: string;
	/** First day of the month, YYYY-MM-01. */
	month: string;
	budget: number;
	spent: number;
	days_left: number;
	allowance: number;
};

export function monthOf(day: string): string {
	return `${day.slice(0, 7)}-01`;
}

function daysInMonth(day: string): number {
	const [y, m] = day.split("-").map(Number);
	return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Spending that counts against the allowance: spendable or uncategorised expenses, plus every fee (D40, D42). */
export function countedSpending(t: Transaction, spendable: Set<string>): number {
	const expense =
		t.kind === "expense" && (t.category_id === null || spendable.has(t.category_id)) ? t.amount : 0;
	return expense + t.fee;
}

/**
 * Daily allowance (FN-4, D42): what is left of this month's spendable budget,
 * spread over the days left including today, floored, never below zero.
 * The server's GET /budgets/allowance computes the same thing.
 */
export function computeAllowance(
	date: string,
	budgets: Budget[],
	categories: Category[],
	transactions: Transaction[],
	timeZone = APP_TIMEZONE,
): Allowance {
	const month = monthOf(date);
	// D45: a category's spendable flag counts even after it is deleted, the
	// same rule as the server, so deleting a category never rewrites history.
	const spendable = new Set(
		categories.filter((c) => c.kind === "expense" && c.spendable).map((c) => c.id),
	);
	const budget = live(budgets)
		.filter((b) => b.month === month && spendable.has(b.category_id))
		.reduce((sum, b) => sum + b.amount, 0);
	const spent = live(transactions)
		.filter((t) => monthOf(localDateKey(new Date(t.occurred_at), timeZone)) === month)
		.reduce((sum, t) => sum + countedSpending(t, spendable), 0);

	const days_left = daysInMonth(date) - Number(date.slice(8, 10)) + 1;
	// Integers below 2^53 divided by at most 31: floor of the float quotient is exact.
	const allowance = Math.max(0, Math.floor((budget - spent) / days_left));
	for (const n of [budget, spent, allowance]) assertUGX(n);
	return { date, month, budget, spent, days_left, allowance };
}

export type MonthSummary = {
	income: number;
	/** Expense amounts, without fees. */
	expense: number;
	/** Every fee that month, shown as its own expense line (D40). */
	fees: number;
	/** Expense amount per category id; uncategorised under "". */
	byCategory: Map<string, number>;
	/** Income amount per stream id; no stream under "". */
	byStream: Map<string, number>;
};

/** Totals for one Kampala month (`month` is YYYY-MM-01). Transfers only add their fee. */
export function monthSummary(
	month: string,
	transactions: Transaction[],
	timeZone = APP_TIMEZONE,
): MonthSummary {
	const s: MonthSummary = {
		income: 0,
		expense: 0,
		fees: 0,
		byCategory: new Map(),
		byStream: new Map(),
	};
	const add = (m: Map<string, number>, key: string | null, n: number) =>
		m.set(key ?? "", (m.get(key ?? "") ?? 0) + n);
	for (const t of live(transactions)) {
		if (monthOf(localDateKey(new Date(t.occurred_at), timeZone)) !== month) continue;
		s.fees += t.fee;
		if (t.kind === "income") {
			s.income += t.amount;
			add(s.byStream, t.stream_id, t.amount);
		} else if (t.kind === "expense") {
			s.expense += t.amount;
			add(s.byCategory, t.category_id, t.amount);
		}
	}
	return s;
}
